import { SlidersHorizontal } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import useAccountLimitsMutations from "../../datahooks/useAccountLimitsMutations";
import { startAccountsBulkAction } from "../../utils";
import AccountLimits from "../AccountLimits";

import type { AccountLimits as AccountLimitsType } from "../../types";

export default function ManageInteractions({
  selectedAccounts,
  onBack,
  onClose,
}: {
  selectedAccounts: WorkspaceAccount[];
  onBack?: () => void;
  onClose: (shouldClearSelection: boolean) => void;
}) {
  const { permissions } = useSelectedWorkspaceContext();
  const isDesktop = useTwBreakpoint("lg");

  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const [accountLimitsState, setAccountLimitsState] =
    useState<AccountLimitsType>({
      connect: 100,
      inmail: 100,
      warmup: { enabled: false },
    });

  const { setAccountLimits } = useAccountLimitsMutations();

  const userAccounts = useMemo(
    () => selectedAccounts.filter(({ type }) => type === "user"),
    [selectedAccounts],
  );

  const rentedAccountsCount = selectedAccounts.length - userAccounts.length;

  function updateInteractions() {
    startAccountsBulkAction(
      (account, abortSignal) =>
        setAccountLimits({
          accountId: account.id,
          limits: accountLimitsState,
          abortSignal,
        }),
      userAccounts,
      (finishedItemsCount) =>
        finishedItemsCount === userAccounts.length
          ? "Interaction limits updated"
          : `Changing ${finishedItemsCount}/${userAccounts.length} interaction limits`,
    );
    onClose(true);
  }

  const isViewOnly = !permissions.includes("accounts_full");

  const title = "Manage interactions";
  const description = `Change weekly interaction limits for ${userAccounts.length === 1 ? userAccounts[0].full_name : `${userAccounts.length} accounts`}?`;

  return (
    <>
      <Header title={title} subtitle={description} onBack={onBack} />

      <Body className="gap-y-2">
        <div className="px-2 py-2.5 flex text-button-sm gap-x-1.5">
          <SlidersHorizontal size={20} weight="fill" />
          Interaction limits / week
        </div>
        <AccountLimits
          accountLimits={accountLimitsState}
          setAccountLimits={setAccountLimitsState}
          isLoading={false}
          isViewOnly={isViewOnly}
        />
        {rentedAccountsCount > 0 && (
          <Alert variant="warning">
            Any changes to interactions settings will not affect{" "}
            {rentedAccountsCount} selected Outreach Avatar(s)
          </Alert>
        )}
      </Body>

      <Footer className="justify-end">
        <Button variant="secondary-black" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button onClick={updateInteractions}>Save</Button>
      </Footer>
    </>
  );
}
